import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { useSearchParams } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Dropdown } from "flowbite-react";
import { usePermissions } from "../utils/permissions";
import { Cap, Level } from "../utils/rbacCatalog";
import { useCreateroup, useDeleteGroups, useGetGroups } from "../hooks/groups";
import Pagination from "../components/public/Pagination";
import {
  HiOutlineExclamationCircle,
  HiUserGroup,
  HiChatAlt2,
  HiViewGrid,
  HiAdjustments,
  HiOutlineTrash,
} from "react-icons/hi";
import { CustomInput } from "../components/atoms/CustomInput";
import { CustomButton } from "../components/atoms/CustomButton";
import { ModalShell } from "../components/atoms/ModalShell";
import { GroupsCardView } from "../components/pages/dashboard/groups/GroupsCardView";
import { CharacterCounter } from "../components/atoms/CharacterCounter";
import NoTranslate from "../components/atoms/NoTranslate";
import ViewEntityGroup from "./ViewEntityGroup";
import { groupSchema } from "./groupSchema";

const Groups = () => {
  const [t] = useTranslation();
  const { user, customer_identification, userEndedSubscription } = useAuth();
  const permissions = usePermissions(user);
  const canWriteGroups = permissions.can(Cap.GROUP_MANAGEMENT, Level.WRITE);
  const canDeleteGroups = permissions.can(Cap.GROUP_MANAGEMENT, Level.DELETE);

  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [limit, setLimit] = useState<12 | 30 | 50>(
    localStorage.getItem("umd_groups_list_limit") &&
      [12, 30, 50].includes(
        Number(localStorage.getItem("umd_groups_list_limit")) as any
      )
      ? (Number(localStorage.getItem("umd_groups_list_limit")) as any)
      : 12
  );
  const [offset, setOffset] = useState(0);
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC" | "">("DESC");
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [openModal, setOpenModal] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedGroupId = searchParams.get("group") ?? "";
  const closeGroupDrawer = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("group");
      return next;
    });
  };

  const protectedGroups = ["BUSINESS CARDS"];

  const {
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    control,
  } = useForm({
    resolver: joiResolver(groupSchema),
  });

  const {
    isLoading,
    isError,
    isSuccess,
    data: res,
    refetch,
  } = useGetGroups({
    customer_identification: customer_identification,
    entityIdentification: user.entity_identification,
    limit: limit,
    pageParam: offset,
    sortOrder: sortOrder,
  });
  const { mutate: createGroup, isLoading: isCreatingGroup } = useCreateroup();
  const { mutate: deleteGroups, isLoading: isDeletingGroup } =
    useDeleteGroups();

  useEffect(() => {
    setLimit(res?.pagination.limit ?? limit);
    const newOffset = res?.pagination.offset ?? offset;
    setOffset(Math.max(0, newOffset));
  }, [res]);

  const handleShowAddGroup = () => {
    reset({
      group_name: "",
      group_description: "",
    });
    setShowCreateGroupModal(true);
  };

  const onSubmit = (values: any) => {
    createGroup(
      {
        customer_identification: customer_identification,
        entityIdentification: user.entity_identification,
        data: values,
      },
      {
        onSuccess: () => {
          reset();
          setShowCreateGroupModal(false);
          refetch();
        },
      }
    );
  };

  const toggleRowSelection = (rowId: string) => {
    setSelectedRowIds((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(rowId)) {
        newSelected.delete(rowId);
      } else {
        newSelected.add(rowId);
      }
      return newSelected;
    });
  };
  const handlePageChange = (pageNumber: number) => {
    if (res?.groups?.length > 0) {
      setOffset(pageNumber * limit);
    }
  };

  const calculateCurrentPage = useCallback(() => {
    const totalItems = res?.pagination.total ?? 0;

    if (limit <= 0) {
      throw new Error("Limit must be a positive integer.");
    }

    if (offset < 0) {
      throw new Error("Offset cannot be negative.");
    }

    if (totalItems === 0) {
      return 0;
    }

    let newOffset = Math.min(offset, Math.max(0, totalItems - 1));

    const currentPage = Math.floor(newOffset / limit);

    return currentPage;
  }, [limit, offset, res?.pagination.total]);

  const isProtectedGroupSelected = () => {
    if (!res?.groups) return false;

    return res.groups.some(
      (group: any) =>
        selectedRowIds.has(group.group_identification) &&
        protectedGroups.includes(group.group_name)
    );
  };

  const handleDeleteGroup = () => {
    deleteGroups(
      {
        customer_identification: customer_identification,
        entityIdentification: user.entity_identification,
        groups: Array.from(selectedRowIds.values()),
      },
      {
        onSuccess: () => {
          setOpenModal(false);
          setSelectedRowIds(new Set());
          refetch();
        },
      }
    );
  };

  let content;

  if (isLoading) content = <div className="p-4">Loading...</div>;

  if (isError)
    content = (
      <div className="p-4">
        <CustomButton
          variant="primary"
          title={t("dashboard.groupList.btn-01") as string}
          onClick={() => refetch()}
        />
      </div>
    );
  const handleSelectAll = (checked: boolean) => {
    setSelectedRowIds(
      checked
        ? new Set(res?.groups?.map((item: any) => item?.group_identification))
        : new Set()
    );
  };

  if (isSuccess)
    content = (
      <React.Fragment>
        {res?.groups?.length === 0 ? (
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex items-start gap-4 mb-3 sm:mb-5">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <HiUserGroup size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-gray-900 text-lg font-bold font-figtree mb-1">
                  {t("dashboard.groupList.promo.title")}
                </h3>
                <p className="text-gray-700 text-sm">
                  {t("dashboard.groupList.promo.description")}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white/70 backdrop-blur-sm rounded-lg p-3 border border-blue-100 hover:border-blue-300 transition-colors">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <HiChatAlt2 className="text-green-600" size={20} />
                  </div>
                  <h4 className="text-xs font-semibold text-gray-900">
                    {t("dashboard.groupList.promo.benefit1")}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {t("dashboard.groupList.promo.benefit1Desc")}
                  </p>
                </div>
              </div>

              <div className="bg-white/70 backdrop-blur-sm rounded-lg p-3 border border-blue-100 hover:border-blue-300 transition-colors">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <HiViewGrid className="text-amber-600" size={20} />
                  </div>
                  <h4 className="text-xs font-semibold text-gray-900">
                    {t("dashboard.groupList.promo.benefit2")}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {t("dashboard.groupList.promo.benefit2Desc")}
                  </p>
                </div>
              </div>

              <div className="bg-white/70 backdrop-blur-sm rounded-lg p-3 border border-blue-100 hover:border-blue-300 transition-colors">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <HiAdjustments className="text-blue-600" size={20} />
                  </div>
                  <h4 className="text-xs font-semibold text-gray-900">
                    {t("dashboard.groupList.promo.benefit3")}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {t("dashboard.groupList.promo.benefit3Desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-3">
            <GroupsCardView
              groups={res?.groups || []}
              selectedRowIds={selectedRowIds}
              toggleRowSelection={toggleRowSelection}
              selectAll={
                selectedRowIds.size === res?.groups?.length &&
                res?.groups?.length > 0
              }
              onSelectAll={handleSelectAll}
              protectedGroups={protectedGroups}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
          </div>
        )}

        {res?.pagination.total > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 my-6 px-2">
            <div className="text-sm text-gray-600 text-center sm:text-left">
              <span className="font-normal">
                {t("dashboard.usersList.key2")}{" "}
              </span>
              <span className="font-semibold">{offset + 1}</span>
              <span className="font-normal">
                {" "}
                {t("dashboard.usersList.key3")}{" "}
              </span>
              <span className="font-semibold">
                {res?.groups?.length ? res?.groups?.length + offset : 0}
              </span>
              <span className="font-normal">
                {" "}
                {t("dashboard.usersList.key4")}{" "}
              </span>
              <span className="font-semibold">
                {res?.pagination.total ?? 0}
              </span>
              <span className="font-normal">
                {" "}
                {t("dashboard.usersList.key5")}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Dropdown
                label={`${limit}`}
                dismissOnClick
                color={"light"}
                className="min-w-[44px]"
                disabled={res?.groups?.length === 0}
              >
                {[12, 30, 50].map((item: any) => (
                  <Dropdown.Item
                    key={item}
                    onClick={() => {
                      localStorage.setItem(
                        "umd_groups_list_limit",
                        item.toString()
                      );
                      setLimit(item);
                      setOffset(0);
                    }}
                  >
                    <NoTranslate>{item}</NoTranslate>
                  </Dropdown.Item>
                ))}
              </Dropdown>

              <Pagination
                pageCount={res?.pagination.total_pages || 0}
                currentPage={calculateCurrentPage()}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        )}
      </React.Fragment>
    );

  const title = (
    <div className="min-w-0">
      <h1 className="text-xl font-semibold text-gray-900">
        {t("dashboard.groupList.title-01")}
      </h1>
      {isSuccess && typeof res?.pagination?.total === "number" && (
        <p className="mt-0.5 text-sm text-gray-500">
          {t("dashboard.groupList.pageSubtitle", {
            count: res.pagination.total,
          })}
        </p>
      )}
    </div>
  );
  const addGroupButton = canWriteGroups ? (
    <CustomButton
      variant="primary"
      disabled={userEndedSubscription}
      onClick={handleShowAddGroup}
      icon="plus"
      title={t("dashboard.groupList.btn-03") as string}
    />
  ) : null;
  const otherButtons = (
    <>
      {canDeleteGroups &&
        selectedRowIds?.size > 0 &&
        !isProtectedGroupSelected() && (
          <CustomButton
            variant="danger"
            onClick={() => setOpenModal(true)}
            title={t("dashboard.groupList.deleteGroups") as string}
            icon={<HiOutlineTrash size={18} />}
          />
        )}
    </>
  );
  return (
    <div className="groups-page">
      <Helmet>
        <title>Groups | Super User Manager</title>
        <meta name="description" content="List of groups" />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/control-panel/groups`}
        />
      </Helmet>

      <div className="container mx-auto py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          {title}
          <div className="flex items-center gap-2">
            {otherButtons}
            {addGroupButton}
          </div>
        </div>

        {content}
      </div>

      <ModalShell
        show={showCreateGroupModal}
        onClose={() => setShowCreateGroupModal(false)}
        title={t("dashboard.groupList.key2")}
        onSubmit={handleSubmit(onSubmit)}
        footer={
          <CustomButton
            type="submit"
            disabled={isCreatingGroup}
            isLoading={isCreatingGroup}
            variant="primary"
            title={t("common.add") as string}
          />
        }
      >
        <div>
          <div className="mb-6">
            <CustomInput
              name="group_name"
              control={control}
              error={errors.group_name}
              helperText={
                errors.group_name?.message
                  ? (t(errors.group_name.message as string) as string)
                  : undefined
              }
              label={t("dashboard.groupList.label-01") as string}
              placeholder={`${t("dashboard.groupList.placeholder-01")}`}
              maxLength={30}
            />
            <CharacterCounter
              warningThreshold={1}
              currentLength={watch("group_name")?.length || 0}
              maxLength={30}
            />
          </div>

          <div className="mb-6">
            <CustomInput
              rows={6}
              name="group_description"
              control={control}
              error={errors.group_description}
              helperText={
                errors.group_description?.message
                  ? (t(errors.group_description.message as string) as string)
                  : undefined
              }
              placeholder={t("dashboard.groupList.placeholder-02") as string}
              label={t("dashboard.groupList.label-02") as string}
              maxLength={250}
            />
            <CharacterCounter
              currentLength={watch("group_description")?.length || 0}
              maxLength={250}
            />
          </div>
        </div>
      </ModalShell>

      <ModalShell
        show={openModal}
        size="md"
        onClose={() => setOpenModal(false)}
        bareCloseButton
        footer={
          <>
            <CustomButton
              onClick={() => {
                setOpenModal(false);
                setSelectedRowIds(new Set());
              }}
              title={t("dashboard.groupList.key5") as string}
            />
            <CustomButton
              variant="danger"
              onClick={handleDeleteGroup}
              isLoading={isDeletingGroup}
              disabled={isDeletingGroup}
              title={t("dashboard.groupList.key4") as string}
            />
          </>
        }
      >
        <div className="text-center">
          <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">
            {t("dashboard.groupList.deleteGroupsTitle")}
          </h3>
        </div>
      </ModalShell>

      {selectedGroupId && (
        <ViewEntityGroup
          key={selectedGroupId}
          groupIdentification={selectedGroupId}
          onClose={closeGroupDrawer}
        />
      )}
    </div>
  );
};

export default Groups;
